import { useEffect, useRef, useState } from 'react';

// Only one mounted instance should claim each drag session. When multiple
// UploadButton instances are on screen (e.g. desktop top-bar + mobile header
// on home.tsx), both register window listeners. Without a lock, both open an
// Upload modal on the same drop. The first instance to see dragenter claims the
// session; all others ignore it until the claim is released on drop/dragend.
let activeDragOwner: symbol | null = null;
const handledDrops = new WeakSet<DragEvent>();

export function useDragToUpload(active: boolean) {
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [droppedFolderId, setDroppedFolderId] = useState<number | undefined>();
  const dragCounter = useRef(0);
  const instanceId = useRef(Symbol());

  useEffect(() => {
    setIsDragging(false);
    dragCounter.current = 0;
    if (activeDragOwner === instanceId.current) activeDragOwner = null;

    if (!active) return;

    const id = instanceId.current;

    const hasFiles = (e: DragEvent) =>
      !!e.dataTransfer?.types &&
      Array.from(e.dataTransfer.types).includes('Files');

    const isUploadModalOpen = () =>
      !!document.querySelector('[data-uppy-theme]');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      if (isUploadModalOpen()) return;
      if (activeDragOwner !== null && activeDragOwner !== id) return;
      if (activeDragOwner === null) activeDragOwner = id;
      dragCounter.current++;
      if (dragCounter.current === 1) setIsDragging(true);
    };

    const onDragLeave = (e: DragEvent) => {
      if (activeDragOwner !== id) return;
      if (!hasFiles(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) {
        setIsDragging(false);
        activeDragOwner = null;
      }
    };

    const onDragOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };

    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      if (isUploadModalOpen() || handledDrops.has(e)) return;
      if (activeDragOwner !== null && activeDragOwner !== id) return;
      handledDrops.add(e);
      activeDragOwner = null;
      const files = Array.from(e.dataTransfer?.files ?? []);
      const folder =
        e.target instanceof Element
          ? e.target.closest<HTMLElement>('[data-upload-folder-id]')
          : null;
      setDroppedFolderId(
        folder ? Number(folder.dataset.uploadFolderId) : undefined
      );
      if (files.length) setDroppedFiles(files);
    };

    const onDragEnd = () => {
      dragCounter.current = 0;
      setIsDragging(false);
      if (activeDragOwner === id) activeDragOwner = null;
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', onDragEnd);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', onDragEnd);
      if (activeDragOwner === id) activeDragOwner = null;
    };
  }, [active]);

  return {
    isDragging,
    droppedFiles,
    droppedFolderId,
    clearDroppedFiles: () => {
      setDroppedFiles([]);
      setDroppedFolderId(undefined);
    }
  };
}
